import { useEffect, useState, type FormEvent } from "react";
import { useI18n } from "../i18n";
import { createInvite, loadDirectory, loadInvites, loadRoles, revokeInvite, type Invite } from "../data";
import type { DirectoryRow, Workspace } from "../types";

/** HR invites people by email; they sign up with that email and join automatically. */
export function Invites({ ws, onChanged }: { ws: Workspace; onChanged?: () => void }) {
  const { t, pick } = useI18n();
  const companyId = ws.membership.company_id;
  const company = pick(ws.membership.company.name, ws.membership.company.name_ar);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [roles, setRoles] = useState<{ key: string; name_en: string; name_ar: string }[]>([]);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [f, setF] = useState({ email: "", role: "employee", employment: "" });
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => loadInvites(companyId).then(setInvites).catch((e) => setError(e.message));
  useEffect(() => {
    void refresh();
    void loadRoles(companyId).then(setRoles);
    if (ws.permissions.has("employees.read")) void loadDirectory(companyId).then((d) => setPeople(d.filter((p) => p.status !== "terminated")));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const message = (i: Invite) =>
    t("inviteMessage").replace("{company}", company).replace("{link}", window.location.origin).replace("{email}", i.email);

  async function copy(i: Invite) {
    try {
      await navigator.clipboard.writeText(message(i));
      setCopied(i.id);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      window.prompt(t("copyMessage"), message(i));
    }
  }

  async function invite(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await createInvite(companyId, f.email.trim(), f.role, f.employment || null);
      setF({ email: "", role: f.role, employment: "" });
      await refresh();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }

  async function revoke(i: Invite) {
    setError(null);
    try { await revokeInvite(i.id); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : t("errorGeneric")); }
  }

  return (
    <section className="card">
      <h2>{t("inviteTitle")}</h2>
      <p className="muted">{t("inviteHelp")}</p>
      <form className="inline-form" onSubmit={invite}>
        <div className="field-row">
          <label className="field"><span>{t("inviteEmail")}</span>
            <input id="inv-email" type="email" dir="ltr" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></label>
          <label className="field"><span>{t("role")}</span>
            <select id="inv-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
              {roles.map((r) => <option key={r.key} value={r.key}>{pick(r.name_en, r.name_ar)}</option>)}
            </select></label>
          {people.length > 0 && (
            <label className="field"><span>{t("linkedEmployee")} <em>{t("optional")}</em></span>
              <select id="inv-emp" value={f.employment} onChange={(e) => setF({ ...f, employment: e.target.value })}>
                <option value="">{t("notLinked")}</option>
                {people.map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)} · {p.employee_number}</option>)}
              </select></label>
          )}
        </div>
        <div className="actions"><button className="btn primary" disabled={!f.email.trim()}>{t("sendInvite")}</button></div>
      </form>
      {error && <p className="alert danger" role="alert">{error}</p>}
      {invites.length > 0 && (
        <>
          <h3 className="sub-head">{t("pendingInvites")}</h3>
          <ul className="plain-list">
            {invites.map((i) => (
              <li key={i.id} className="invite-row">
                <div>
                  <div className="strong" dir="ltr">{i.email}</div>
                  <div className="muted small-note">{pick(roles.find((r) => r.key === i.role_key)?.name_en ?? i.role_key, roles.find((r) => r.key === i.role_key)?.name_ar)}</div>
                </div>
                <div className="row">
                  <button type="button" className="btn secondary small" onClick={() => copy(i)}>{copied === i.id ? t("copied") : t("copyMessage")}</button>
                  <button type="button" className="btn secondary small" onClick={() => revoke(i)}>{t("revoke")}</button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
